import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Calendar from 'react-calendar';

import Switch from '../../../components/shared/switch';
import Modal from '../../../components/shared/modal';

import * as dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';
import TimeInput from "react-time-picker-input";

dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(weekday);

const DateDialog = ({ close, onSaveDates, initialDates = {} }) => {
  const [show, setShow] = useState(true);
  const [isRegular, setIsRegular] = useState(false);
  const { t, i18n } = useTranslation();

  const [selectedDates, setSelectedDates] = useState([]);

  const [selectedRegularDates, setSelectedRegularDates] = useState([]);
  const [range, setRange] = useState([null, null]);
  const [selectedWeekDays, setSelectedWeekDays] = useState([]);
  const [weekDayTimes, setWeekDayTimes] = useState([]);

  const formatDate = date => dayjs(date).format('YYYY-MM-DD');

  useEffect(() => {
    const { dates, range, isRegular, weekDays, time } = initialDates;
    setIsRegular(isRegular);
    setRange(range ? [range.start, range.end] : []);
    setSelectedWeekDays(weekDays || []);
    setWeekDayTimes(time || []);

    if (dates) {
      const initializedDates = dates.reduce((acc, date) => {
        const existingDate = acc.find(d => d.date === formatDate(date));
        existingDate ? existingDate.count++ : acc.push({ date: formatDate(date), count: 1 });
        return acc;
      }, []);
      isRegular ? setSelectedRegularDates(initializedDates) : setSelectedDates(initializedDates);
    }
  }, [initialDates]);

  const closeWindow = () => {
    setShow(false);
    close && close();
  };

  const saveDates = () => {
    const expandedDates =
      isRegular && range[0] && range[1]
        ? Array.from({ length: dayjs(range[1]).diff(dayjs(range[0]), 'days') + 1 }, (_, i) => {
            const currentDate = dayjs(range[0]).add(i, 'day');
            const dayConfig = selectedWeekDays.find(day => day.index === currentDate.weekday());
            const times = weekDayTimes.find(d => d.dayIndex === currentDate.weekday())?.times || [];
            return dayConfig ? times.map(time => [currentDate.format('YYYY-MM-DD'), time]) : [];
          }).flat()
        : selectedDates.flatMap(d => Array(d.count).fill(d.date));

    onSaveDates({
      dates: expandedDates,
      isRegular,
      range: isRegular ? { start: range[0], end: range[1] } : {},
      weekDays: isRegular ? selectedWeekDays : [],
      time: isRegular ? weekDayTimes : [],
    });

    closeWindow();
  };

  const dateChange = date => {
    setSelectedDates(prevDates => {
      const dateIndex = prevDates.findIndex(d => d.date === formatDate(date));
      if (dateIndex === -1) return [...prevDates, { date: formatDate(date), count: 1 }];

      const updatedDates = [...prevDates];
      const count = (updatedDates[dateIndex].count + 1) % 4;
      if (count) updatedDates[dateIndex].count = count;
      else updatedDates.splice(dateIndex, 1);

      return updatedDates;
    });
  };

  const dateRightClick = (event, date) => {
    if (!isRegular) {
      event.preventDefault();
      setSelectedDates(prevDates =>
        prevDates
          .map(d => (d.date === formatDate(date) ? { ...d, count: Math.max(0, d.count - 1) } : d))
          .filter(d => d.count > 0)
      );
    }
  };

  const renderDots = ({ date }) => {
    const selectedData = isRegular ? selectedRegularDates : selectedDates;
    const dotCount = Math.min((selectedData.find(d => d.date === formatDate(date))?.count || 0) - 1, 2);

    return (
      <div
        onContextMenu={e => {
          e.preventDefault();
          dateRightClick(e, date);
        }}
      >
        <div className={`dots dot-${dotCount}`}>
          {Array.from({ length: dotCount }, (_, index) => (
            <div key={index} className="dot" />
          ))}
        </div>
      </div>
    );
  };

  const dayWeekSelection = dayIndex => {
    if (!range[0] || !range[1]) return;

    setSelectedWeekDays(prev => {
      const updatedWeekDays = [...prev];
      const existingIndex = updatedWeekDays.findIndex(day => day.index === dayIndex);

      if (existingIndex !== -1) {
        updatedWeekDays[existingIndex].count = (updatedWeekDays[existingIndex].count + 1) % 4;
        if (updatedWeekDays[existingIndex].count === 0) updatedWeekDays.splice(existingIndex, 1);
      } else {
        updatedWeekDays.push({ index: dayIndex, count: 1 });
      }

      const newSelectedDates = updatedWeekDays.reduce((dates, day) => {
        if (day.count > 0) {
          let dayDate = dayjs(range[0]).weekday(day.index);
          while (dayDate.isBefore(dayjs(range[1]), 'day') || dayDate.isSame(dayjs(range[1]), 'day')) {
            if (dayDate.isSameOrAfter(dayjs(range[0]), 'day')) {
              dates.push({ date: dayDate.format('YYYY-MM-DD'), count: day.count });
            }
            dayDate = dayDate.add(1, 'week');
          }
        }
        return dates;
      }, []);

      setSelectedRegularDates(newSelectedDates);
      return updatedWeekDays;
    });

    setWeekDayTimes(prev => {
      const updatedTimes = [...prev];
      const existingDayIndex = updatedTimes.findIndex(d => d.dayIndex === dayIndex);

      if (existingDayIndex !== -1) {
        const existingDay = updatedTimes[existingDayIndex];
        existingDay.times = existingDay.times.length < 3 ? [...existingDay.times, ''] : [];
      } else {
        updatedTimes.push({ dayIndex, times: [''] });
      }

      return updatedTimes.filter(day => day.times.length > 0);
    });
  };

  const weekDayRightClick = (event, dayIndex) => {
    event.preventDefault();
    setSelectedWeekDays(prev => {
      const existingIndex = prev.findIndex(day => day.index === dayIndex);
      const updatedWeekDays = [...prev];

      if (existingIndex !== -1) {
        updatedWeekDays[existingIndex].count = Math.max(0, updatedWeekDays[existingIndex].count - 1);
        if (updatedWeekDays[existingIndex].count === 0) {
          updatedWeekDays.splice(existingIndex, 1);
        }
      }

      setSelectedRegularDates(prevDates => {
        const updatedDates = prevDates
          .map(d => {
            const date = dayjs(d.date);
            const newCount = d.count - (dayIndex === date.weekday() ? 1 : 0);
            return newCount > 0 ? { ...d, count: newCount } : null;
          })
          .filter(d => d !== null);
        return updatedDates;
      });

      return updatedWeekDays;
    });

    setWeekDayTimes(prevTimes =>
      prevTimes.map(day => (day.dayIndex === dayIndex ? { ...day, times: day.times.slice(0, -1) } : day))
    );
  };

  const renderWeekDayDots = dayIndex => {
    const dayConfig = selectedWeekDays.find(d => d.index === dayIndex);
    let dotCount = dayConfig ? Math.min(dayConfig.count - 1, 2) : 0;

    return (
      <div className={`dots dot-${dotCount}`} onContextMenu={e => weekDayRightClick(e, dayIndex)}>
        {Array.from({ length: dotCount }, (_, index) => (
          <div key={index} className="dot" />
        ))}
      </div>
    );
  };

  const weekDays = Array.from({ length: 7 }).map((_, index) => {
    return dayjs().weekday(index).format('dd');
  });

  const tileClassName = ({ date, view }) => {
    if (view !== 'month') return '';

    const isInRange = range[0] && range[1] && dayjs(date).isBetween(dayjs(range[0]), dayjs(range[1]), 'day', '[]');
    const isSelected = datesList => datesList.some(d => dayjs(d.date).format('YYYY-MM-DD') === formatDate(date));
    const isSelectedRegular = isSelected(selectedRegularDates);
    const isSelectedNormal = isSelected(selectedDates);

    return isRegular && isInRange
      ? isSelectedRegular
        ? 'has-event in-range'
        : 'in-range'
      : !isRegular && isSelectedNormal && 'has-event';
  };

  const handleTimeChange = (dayIndex, timeIndex, value) => {
    setWeekDayTimes(prev =>
      prev.map(d =>
        d.dayIndex === dayIndex ? { ...d, times: d.times.map((time, idx) => (idx === timeIndex ? value : time)) } : d
      )
    );
  };

  const renderTimeInputs = dayIndex => {
    const dayTimes = weekDayTimes.find(day => day.dayIndex === dayIndex)?.times || [];

    return (
      <>
        {dayTimes.map((time, index) => (
          <div className="time-inputs" key={index}>
            <TimeInput value={time} onChange={e => handleTimeChange(dayIndex, index, e)}/>
          </div>
        ))}


      </>
    );
  };

  return (
    <Modal openModal={show} closeModal={closeWindow}>
      <div className="data-dialog">
        <div className="dialog-header">
          <div className="dialog-header-title">{t('schedule.select_dates')}</div>
          <div className="selected-range">
            <div className="dialog-header-name">{t('schedule.practice_schedule')}</div>
            <div className="selected-buttons">
              {t('schedule.regular_dates')}
              <Switch value={isRegular} onChange={value => setIsRegular(value)} />
            </div>
          </div>
        </div>
        <div className="flex1 flex-between">
          <div className="calendar-section">
            <div className="calendar-editor-scene">
              <Calendar
                value={null}
                locale={i18n.language}
                onClickDay={isRegular ? null : dateChange}
                formatDay={(_, date) => dayjs(date).format('D')}
                formatMonthYear={(_, date) => dayjs(date).format('MMM YYYY')}
                tileClassName={tileClassName}
                tileContent={renderDots}
                tileDisabled={({ date }) => dayjs(date).isBefore(dayjs().startOf('day'))}
                selectRange={isRegular}
                onChange={isRegular ? setRange : null}
              />
            </div>
          </div>
          <div className={`time-picker-section ${isRegular ? 'expanded' : ''}`} style={{'--count': Math.max(...selectedWeekDays.map(day => day.count), 1)}}>
            {isRegular && (
              <div className="week-days">
                <div className="week-day-text">
                  {Array.from({ length: Math.max(...selectedWeekDays.map(day => day.count), -1) }).map((_, index) => (
                    <div className="week-item" key={index}> {t('schedule.time')}</div>
                  ))}
                </div>
                {weekDays.map((day, index) => (
                  <div className="week-day-row" key={index}>
                    <div
                      className={`week-day-circle ${selectedWeekDays.some(d => d.index === index) ? 'selected' : ''} ${!range[0] || !range[1] ? 'disabled' : ''}`}
                      onClick={() => dayWeekSelection(index)}
                    >
                      {day}{renderWeekDayDots(index)}
                    </div>
                    {renderTimeInputs(index)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="selected-range">
          <div className={`selected-time-wrapper ${isRegular ? 'visible' : ''}`} >
            {t('schedule.selected_range')} {range[0] ? dayjs(range[0]).format('MMM D, YYYY') : t('schedule.start_date')} - {range[1] ? dayjs(range[1]).format('MMM D, YYYY') : t('schedule.end_date')}
          </div>
          <div className="selected-buttons">
            <button className="button" onClick={closeWindow}>{t('buttons.common.cancel')}</button>
            <button className="button button-primary" onClick={saveDates}>{t('buttons.common.save')}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DateDialog;
